import type { DeckorateCommand, Tab } from "../../../types";
import { logError, logEvent, scrubMessage } from "../../../../../telemetry";

// Generous on purpose: resolution is MutationObserver-driven (see
// waitForElement), so a longer ceiling costs nothing on the happy path.
// Returning from a real Office Dialog window adds teardown/focus-return
// latency that blew past a 2s budget in testing.
const TAB_SWITCH_TIMEOUT_MS = 6000;

/** Matches catalog.ts's/CommandPalette.tsx's connectorSystemName convention. */
const CMD_PALETTE_CONNECTOR = "deckorate";

/** Reports a command-run failure through the shared (PII-scrubbed) telemetry pipeline. */
function logCmdPaletteError(reason: string, type: string, e: unknown): void {
  logError(
    "error.connector.failure",
    { type, scrubbedMessage: scrubMessage(e instanceof Error ? e.message : String(e)) },
    { connectorSystemName: CMD_PALETTE_CONNECTOR, reason },
  );
}

/** Reports a non-fatal per-target skip inside a domActionSequence — the
 * sequence still proceeds, so this is `warn`, not `error`. */
function logCmdPaletteWarn(reason: string): void {
  logEvent("connector.action.invoke", "warn", { connectorSystemName: CMD_PALETTE_CONNECTOR, reason });
}

/** Defensive backstop: querySelector throws on a malformed selector — treated
 * as "not found yet" rather than crashing the caller. */
function safeQuerySelector(selector: string): HTMLElement | null {
  if (!selector) return null;
  try {
    return document.querySelector<HTMLElement>(selector);
  } catch (e) {
    logCmdPaletteError("cmd-palette-invalid-selector", "InvalidSelectorError", e);
    return null;
  }
}

/** Resolves once an element matching `selector` mounts, or `null` after
 * `timeoutMs` — MutationObserver-based so a slow-mounting tab can't miss the click. */
function waitForElement(selector: string, timeoutMs = TAB_SWITCH_TIMEOUT_MS): Promise<HTMLElement | null> {
  // No document.body means nothing to observe — same as an immediate timeout.
  if (!selector || !document.body) return Promise.resolve(null);

  const existing = safeQuerySelector(selector);
  if (existing) return Promise.resolve(existing);

  return new Promise((resolve) => {
    const cleanup = (): void => {
      window.clearTimeout(timer);
      observer.disconnect();
    };
    const timer = window.setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeoutMs);
    const observer = new MutationObserver(() => {
      const el = safeQuerySelector(selector);
      if (el) {
        cleanup();
        resolve(el);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  });
}

/** Waits one animation frame so React commits a state update before the next
 * click in a domActionSequence fires, avoiding a stale-state closure. */
function settle(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

/**
 * Runs a dom-click/dom-select command: navigates to its tab if needed, waits
 * for the target to mount (waitForElement), then clicks. `domActionSequence`
 * clicks multiple targets in order; only the first needs the mount-wait.
 *
 * Returns `false` on a first-target timeout (a notice was already shown via
 * `showNotice`) so the caller knows the command did not complete.
 */
export async function runDomAction(
  cmd: DeckorateCommand,
  currentTab: Tab,
  onNavigate: (tab: Tab) => void,
  showNotice: (message: string) => void,
): Promise<boolean> {
  if (cmd.tab && cmd.tab !== currentTab) onNavigate(cmd.tab);

  if (cmd.kind === "dom-select" && cmd.selectId && cmd.selectValue) {
    const el = await waitForElement(`[data-cmd-select="${cmd.selectId}"]`);
    if (!el) {
      logCmdPaletteError(
        "cmd-palette-dom-action-timeout",
        "DomSelectTimeout",
        new Error(`Timed out waiting for [data-cmd-select="${cmd.selectId}"]`),
      );
      showNotice(`"${cmd.label}" didn't load in time — try again.`);
      return false;
    }
    (el as HTMLSelectElement).value = cmd.selectValue;
    el.dispatchEvent(new Event("change", { bubbles: true }));
    return true;
  }

  if (cmd.kind !== "dom-click") return true;
  const targets = cmd.domActionSequence ?? (cmd.domAction ? [cmd.domAction] : []);
  if (targets.length === 0) return true;

  const first = await waitForElement(`[data-cmd-action="${targets[0]}"]`);
  if (!first) {
    logCmdPaletteError(
      "cmd-palette-dom-action-timeout",
      "DomClickTimeout",
      new Error(`Timed out waiting for [data-cmd-action="${targets[0]}"]`),
    );
    showNotice(`"${cmd.label}" didn't load in time — try again.`);
    return false;
  }
  first.click();

  // Subsequent targets are expected to already be mounted (see doc comment
  // above); a miss here is warn-only (no user-facing notice) but must still
  // be logged or it's invisible. The sequence still counts as a success.
  for (const domAction of targets.slice(1)) {
    await settle(); // NOSONAR action steps run in sequence with a settle between them
    const el = document.querySelector<HTMLElement>(`[data-cmd-action="${domAction}"]`);
    if (!el) {
      logCmdPaletteWarn("cmd-palette-dom-action-target-missing");
      continue;
    }
    el.click();
  }
  return true;
}
